import * as React from "react";
import { merge, $, media, keyframes } from "glamor";
import { theme } from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import Close from "components/icons/close";
import MoreHorizIcon from "material-ui/svg-icons/navigation/more-horiz";
import { IconMenu, IconButton, MenuItem } from "material-ui";
import SyncIcon from "material-ui/svg-icons/notification/sync";
import { DATATEST } from "constants/integration";
import { IItemReferenceCardProps, ItemReferenceCard } from "./item-reference-card";
import { NAV_PROPS } from "./product-collection-item-modal";
import { LeftArrow, RightArrow } from "./navigation-arrows";
import { List, Map } from "immutable";
import ClickAwayListener from "material-ui/internal/ClickAwayListener";

interface IPropsForModal {
    readOnly: boolean;
    onCloneItem: () => void;
    onRequestClose: () => void;
    onRemoveItem: () => void;
    onNavigateProduct: ({ currentTarget }) => void;
    copiedAllocationName: string;
    copiedAllocation: Map<string, any>;
    copyAllocation: () => void;
    pasteAllocation: () => void;
    sizingAllocationEnabled: boolean;
    sizingRangeEnabled: boolean;
    sizingCurveEnabled: boolean;
    findSizeRanges: () => void;
    allocate: () => void;
    findingSizes: boolean;
    allocatingAssortment: boolean;
    thereIsAllocation?: boolean;
    itemDisplayProperty?: List<any>;
    onAcceptPackUnits: () => void;
    sizePackEnabled?: boolean;
    isServerRollup?: boolean;
}

interface IProps extends IPropsForModal, IItemReferenceCardProps {}

export const ModalTitle: React.FunctionComponent<IProps> = ({
    readOnly,
    onCloneItem,
    onRequestClose,
    onRemoveItem,
    copiedAllocationName,
    copiedAllocation,
    copyAllocation,
    pasteAllocation,
    sizingAllocationEnabled,
    sizingCurveEnabled,
    sizingRangeEnabled,
    allocate,
    findSizeRanges,
    findingSizes,
    allocatingAssortment,
    thereIsAllocation,
    onNavigateProduct,
    getSelectedImageUrl,
    detailFields,
    item,
    brandEntities,
    itemDisplayProperty,
    onAcceptPackUnits,
    sizePackEnabled,
    isServerRollup,
}) => {
    const [openMenu, setOpenMenu] = React.useState(false);

    const spin = React.useMemo(() => {
        return keyframes({
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(-360deg)" },
        });
    }, []);

    const styles = React.useMemo(() => {
        return merge(
            {
                border: `1px solid ${theme.palette.borderColor}`,
                borderRadius: "5px",
                margin: "0px !important",
                padding: "10px 10px 10px !important",
                display: "flex",
                position: "relative",
                minHeight: "96px",
            },
            $(" .spinningWheel", {
                animation: `${spin} 2s infinite linear`,
            }),
            $(" .titleField", {
                fontSize: "18px",
                height: "49px",
                lineHeight: "49px",
            }),
            $(" .titleIcon", {
                margin: "12px 3px",
            }),
            $(" .titleContainer", {
                width: "100%",
                justifyContent: "center",
                display: "flex",
            }),
            $(" .divider", {
                backgroundColor: "#e1e1e1",
                height: "26px",
                margin: "0px 8px",
                width: "1px",
                alignSelf: "center",
            }),
            $(" .iconActions", {
                marginLeft: "10px",
                padding: "5px",
                cursor: "pointer",
                fill: `${theme.palette.darkGrey} !important`,
                height: "30px !important",
                width: "30px !important",
            }),
            $(" .closeIcon", {
                cursor: "pointer",
                fill: `${theme.palette.darkGrey} !important`,
                height: "30px !important",
                width: "30px !important",
                alignSelf: "center",
            }),
            $(" .closeContainer", {
                height: "48px",
                display: "flex",
            }),
            $(" .actions", {
                display: "flex",
                position: "absolute",
                right: "24px",
                zIndex: 100,
            }),
            $(" .navigationArrows", {
                flex: "0 0 auto",
                display: "flex",
            }),
            $(" .navigationArrows button", {
                background: "none",
                border: "none",
            }),
            media(
                "(max-width: 780px)",
                $(" .titleContainer", {
                    display: "none",
                })
            )
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const renderKabobOpenerIcon = React.useMemo(() => {
        if (allocatingAssortment || findingSizes) {
            return <SyncIcon className="spinningWheel" />;
        }
        return <MoreHorizIcon color={theme.palette.darkGrey} />;
    }, [allocatingAssortment, findingSizes]);

    const handleClickItem = (handleClickOption) => () => {
        handleClickOption();
        setOpenMenu(false);
    };

    const iconButtonElementStyle = React.useMemo(() => ({ padding: "0px" }), []);
    return (
        <div {...styles}>
            <div className="navigationArrows">
                <button onClick={onNavigateProduct} {...NAV_PROPS.left}>
                    <LeftArrow width="24" height="24" viewBox="0 0 24 24" />
                </button>
                <button onClick={onNavigateProduct} {...NAV_PROPS.right}>
                    <RightArrow width="24" height="24" viewBox="0 0 24 24" />
                </button>
            </div>
            <ItemReferenceCard
                getSelectedImageUrl={getSelectedImageUrl}
                detailFields={detailFields}
                item={item}
                brandEntities={brandEntities}
                itemDisplayProperty={itemDisplayProperty}
            />
            <div className="actions">
                {!readOnly && (
                    <ClickAwayListener onClickAway={() => setOpenMenu(false)}>
                        <IconMenu
                            key="more"
                            iconButtonElement={
                                <IconButton
                                    data-test={DATATEST.ASSORTMENTS.CDP.CDP_ACTIONS_KABOB}
                                    style={iconButtonElementStyle}
                                >
                                    {renderKabobOpenerIcon}
                                </IconButton>
                            }
                            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                            targetOrigin={{ horizontal: "right", vertical: "bottom" }}
                            onClick={() => setOpenMenu(true)}
                            open={openMenu}
                        >
                            <MenuItem
                                data-test={DATATEST.ASSORTMENTS.CDP.COPY_BTN}
                                primaryText={formatMessage("Duplicate")}
                                onClick={handleClickItem(onCloneItem)}
                            />
                            {sizingAllocationEnabled && (
                                <>
                                    {sizingRangeEnabled && (
                                        <MenuItem
                                            data-test={
                                                findingSizes
                                                    ? DATATEST.ASSORTMENTS.CDP.STOP_SIZE_RANGE_BTN
                                                    : DATATEST.ASSORTMENTS.CDP.FIND_SIZE_RANGE_BTN
                                            }
                                            primaryText={
                                                findingSizes
                                                    ? formatMessage("Stop find size ranges")
                                                    : formatMessage("Find size ranges")
                                            }
                                            onClick={handleClickItem(findSizeRanges)}
                                        />
                                    )}
                                    {sizingCurveEnabled && (
                                        <MenuItem
                                            data-test={
                                                findingSizes
                                                    ? DATATEST.ASSORTMENTS.CDP.STOP_APPLY_CURVES_BTN
                                                    : DATATEST.ASSORTMENTS.CDP.APPLY_SIZE_CURVES_BTN
                                            }
                                            primaryText={
                                                allocatingAssortment
                                                    ? formatMessage("Stop Apply Size Curves")
                                                    : formatMessage("Apply Size Curves")
                                            }
                                            onClick={handleClickItem(allocate)}
                                        />
                                    )}
                                    {sizePackEnabled && (
                                        <MenuItem
                                            primaryText={formatMessage("Accept Pack Units")}
                                            onClick={handleClickItem(onAcceptPackUnits)}
                                        />
                                    )}
                                    <MenuItem
                                        data-test={DATATEST.ASSORTMENTS.CDP.COPY_ALLOCATION_BTN}
                                        disabled={!thereIsAllocation}
                                        primaryText={formatMessage("Copy Allocation")}
                                        onClick={handleClickItem(copyAllocation)}
                                    />
                                    <MenuItem
                                        data-test={DATATEST.ASSORTMENTS.CDP.PASTE_ALLOCATION_BTN}
                                        disabled={!copiedAllocation?.size}
                                        primaryText={
                                            copiedAllocationName
                                                ? formatMessage(`Paste Allocation from ${copiedAllocationName}`)
                                                : formatMessage("Paste Allocation")
                                        }
                                        onClick={handleClickItem(pasteAllocation)}
                                    />
                                </>
                            )}
                            <MenuItem
                                data-test={DATATEST.ASSORTMENTS.CDP.TRASH_BTN}
                                primaryText={formatMessage("Delete")}
                                onClick={handleClickItem(onRemoveItem)}
                            />
                        </IconMenu>
                    </ClickAwayListener>
                )}
                {!readOnly && <div className="divider" />}
                <div className="closeContainer">
                    <Close
                        data-test={DATATEST.ASSORTMENTS.DETAILS_CLOSE_BTN}
                        key="close"
                        className="closeIcon"
                        onClick={onRequestClose}
                        color={theme.palette.darkerGrey}
                    />
                </div>
            </div>
        </div>
    );
};
